import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { ConfirmActionModal } from "../components/ConfirmActionModal";
import { FilterChips } from "../components/FilterChips";
import { Modal } from "../components/Modal";
import { SearchInput } from "../components/SearchInput";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Mono,
  PageHeader,
  Select,
  Table,
  TableSkeleton,
  Tag,
  Td,
  Th,
} from "../components/ui";
import { useCanWrite } from "../auth/AuthProvider";
import { ApiError } from "../lib/api";
import { formatDateTime, formatRelative } from "../lib/format";
import {
  useEmailDomainCheck,
  useEmailDomains,
  useRemoveEmailDomain,
  useSyncEmailDomains,
  useUpsertEmailDomain,
} from "../lib/queries";
import type { EmailDomainRule } from "../lib/types";

const LIMIT = 100;

type Source = "operator" | "list";

const SOURCE_OPTIONS: { value: Source; label: string }[] = [
  { value: "operator", label: "Written here" },
  { value: "list", label: "From the list" },
];

/** What the rule modal opens with. Every way in prefills it; none of them lock a field. */
interface Draft {
  domain: string;
  action: "block" | "allow";
  reason: string;
}

/** Something a person would type as a domain, so a search miss can offer to block it. */
const DOMAIN_SHAPE = /^[a-z0-9.-]+\.[a-z]{2,}$/i;

/**
 * The email domains registration turns away -- migration 0143.
 *
 * Two kinds of row share the table. The daily sync writes `list` rows from a
 * public register of throwaway-mail services and replaces them wholesale, so
 * they are shown but not edited: an edit would not survive the night. The
 * rows an operator writes are the ones that matter here, and they win over
 * the list for the same domain either way -- a `block` for a service the
 * list has not caught up with, an `allow` for one it caught wrongly.
 *
 * The page is arranged around the two questions that bring somebody here.
 * "Why can't this person sign up?" is the check at the top, and its answer
 * carries the next step as a button -- allow the domain, or lift the rule --
 * rather than leaving the operator to copy a domain into a form. "Block this
 * service" is the same form, reached from the header, from a search that
 * found nothing, or from a list row that needs an operator's word over it.
 *
 * Support can read the table and ask what an address would get; writing a
 * rule is superadmin, because a row here turns real people away at the door.
 */
export function EmailDomainsPage() {
  const canWrite = useCanWrite();
  const [q, setQ] = useState("");
  const [source, setSource] = useState<Source | "">("");
  const { data, isPending, error } = useEmailDomains({
    limit: LIMIT,
    ...(q ? { q } : {}),
    ...(source ? { source } : {}),
  });
  const sync = useSyncEmailDomains();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [removing, setRemoving] = useState<EmailDomainRule | null>(null);
  const [syncNote, setSyncNote] = useState<string | null>(null);

  const term = q.trim().toLowerCase();
  const offersBlock = canWrite && DOMAIN_SHAPE.test(term);

  function openRule(next: Partial<Draft> = {}) {
    setDraft({ domain: "", action: "block", reason: "", ...next });
  }

  return (
    <>
      <PageHeader
        title="Email domains"
        description="Addresses on these domains cannot register. The list is refreshed every night; rules written here outlast it."
        action={
          canWrite ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                loading={sync.isPending}
                onClick={() => {
                  setSyncNote(null);
                  sync
                    .mutateAsync()
                    .then((outcome) =>
                      setSyncNote(
                        `Synced ${outcome.fetched.toLocaleString()} domains: ${outcome.inserted.toLocaleString()} added, ${outcome.removed.toLocaleString()} dropped.`,
                      ),
                    )
                    .catch((cause: unknown) =>
                      setSyncNote(cause instanceof ApiError ? cause.message : "The sync did not run."),
                    );
                }}
              >
                Sync list now
              </Button>
              <Button onClick={() => openRule()}>Write a rule</Button>
            </div>
          ) : undefined
        }
      />

      {syncNote && <Alert tone={sync.isError ? "error" : "info"}>{syncNote}</Alert>}

      <CheckCard
        canWrite={canWrite}
        onAllow={(domain) => openRule({ domain, action: "allow" })}
        onBlock={(domain) => openRule({ domain, action: "block" })}
        onLift={(domain) =>
          setRemoving(data?.data.find((rule) => rule.domain === domain) ?? placeholderRule(domain))
        }
      />

      <Card>
        <CardHeader
          title="Rules"
          description={data ? describeCounts(data.counts, data.lastSyncedAt) : undefined}
        />
        {/* One row, filter then search: the chips are a set of three the eye
            reads at once, and the search narrows whichever one is picked. */}
        <div className="flex flex-col gap-3 border-b border-line px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
          <FilterChips
            value={source}
            onChange={setSource}
            options={SOURCE_OPTIONS}
            allLabel="All rules"
            ariaLabel="Filter by source"
          />
          <div className="sm:w-72">
            <SearchInput value={q} onChange={setQ} placeholder="Find a domain…" />
          </div>
        </div>

        {isPending ? (
          <TableSkeleton columns={4} />
        ) : error ? (
          <div className="p-5">
            <Alert>{error.message}</Alert>
          </div>
        ) : data.data.length === 0 ? (
          <EmptyState
            icon="shield"
            title={term ? `No rule mentions ${term}` : "No rules yet"}
            description={
              term
                ? offersBlock
                  ? "Addresses there can register. Block it if they should not."
                  : undefined
                : "Run the sync to load the daily list."
            }
            action={
              offersBlock ? (
                <Button onClick={() => openRule({ domain: term, action: "block" })}>
                  Block {term}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table
            head={
              <tr>
                <Th>Domain</Th>
                <Th>Rule</Th>
                <Th priority="md">Reason</Th>
                <Th priority="lg">Added</Th>
                <Th className="text-right"> </Th>
              </tr>
            }
          >
            {data.data.map((rule) => (
              <tr key={rule.domain} className="hover:bg-raised">
                <Td>
                  <Mono>{rule.domain}</Mono>
                  {/* What the block did not undo. A mass registration leaves
                      its accounts behind, and this is where their number is
                      visible; the users page finds them by the domain. */}
                  {rule.accounts !== null && rule.accounts > 0 && (
                    <p className="mt-0.5 text-xs text-fg-subtle">
                      <Link to={`/users?q=${encodeURIComponent(`@${rule.domain}`)}`} className="hover:text-fg hover:underline">
                        {rule.accounts.toLocaleString()} {rule.accounts === 1 ? "account" : "accounts"} registered
                      </Link>
                    </p>
                  )}
                </Td>
                <Td>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <RuleTag rule={rule} />
                    {/* Said on the row, because it is what Remove will do. */}
                    {rule.source === "operator" && rule.listed && (
                      <span className="text-xs text-fg-subtle">over the list</span>
                    )}
                  </div>
                </Td>
                <Td priority="md">
                  <span className="text-fg-subtle">{rule.reason ?? "—"}</span>
                </Td>
                <Td priority="lg" title={formatDateTime(rule.createdAt)} className="whitespace-nowrap">
                  <span className="text-fg-subtle">
                    {formatRelative(rule.createdAt)}
                    {rule.addedByName ? ` · ${rule.addedByName}` : ""}
                  </span>
                </Td>
                <Td className="text-right">
                  {canWrite && (
                    <div className="flex items-center justify-end gap-3">
                      {rule.source === "list" ? (
                        /* The one thing to do with a list row: put an
                           operator's word over it. Removing it would not
                           stick; the next sync writes it back. */
                        <RowAction onClick={() => openRule({ domain: rule.domain, action: "allow" })}>
                          Allow
                        </RowAction>
                      ) : (
                        <>
                          <RowAction
                            onClick={() =>
                              openRule({ domain: rule.domain, action: rule.action, reason: rule.reason ?? "" })
                            }
                          >
                            Edit
                          </RowAction>
                          <RowAction onClick={() => setRemoving(rule)}>Remove</RowAction>
                        </>
                      )}
                    </div>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        )}
        {data && data.total > data.data.length && (
          <p className="border-t border-line px-5 py-3 text-xs text-fg-subtle">
            {data.data.length} of {data.total.toLocaleString()} shown. Search to narrow it.
          </p>
        )}
      </Card>

      <p className="px-1 text-xs leading-6 text-fg-subtle">
        A rule on a domain covers its subdomains. Between two rules the deeper one wins, and a
        rule written here beats one from the list. Removing a rule written here for a listed
        domain puts the list&apos;s block back. Accounts already registered are never touched.
      </p>

      <RuleModal draft={draft} onClose={() => setDraft(null)} />
      <RemoveModal rule={removing} onClose={() => setRemoving(null)} />
    </>
  );
}

function describeCounts(counts: { operator: number; list: number }, lastSyncedAt: string | null): string {
  const written = `${counts.operator.toLocaleString()} written here`;
  const listed = lastSyncedAt
    ? `${counts.list.toLocaleString()} from the list, synced ${formatRelative(lastSyncedAt)}`
    : "the list has not been synced yet";
  return `${written} · ${listed}.`;
}

/** A row that is not on the loaded page — the check can name a rule the filter hides. */
function placeholderRule(domain: string): EmailDomainRule {
  return {
    domain,
    action: "block",
    source: "operator",
    listed: false,
    reason: null,
    addedByName: null,
    createdAt: "",
    accounts: null,
  };
}

function RuleTag({ rule }: { rule: EmailDomainRule }) {
  if (rule.action === "allow") return <Tag tone="good">Allowed</Tag>;
  return <Tag tone={rule.source === "operator" ? "bad" : "neutral"}>Blocked</Tag>;
}

function RowAction({ onClick, children }: { onClick: () => void; children: string }) {
  return (
    <button type="button" onClick={onClick} className="text-sm text-accent hover:underline">
      {children}
    </button>
  );
}

/**
 * "Would this address get in?" -- the question a ticket actually asks, with
 * the rule that decides it and the one thing to do about it. Asked on submit
 * rather than per keystroke, so half an address is never looked up.
 */
function CheckCard({
  canWrite,
  onAllow,
  onBlock,
  onLift,
}: {
  canWrite: boolean;
  onAllow: (domain: string) => void;
  onBlock: (domain: string) => void;
  onLift: (domain: string) => void;
}) {
  const [draft, setDraft] = useState("");
  const [email, setEmail] = useState("");
  const check = useEmailDomainCheck(email);
  const domain = email.split("@")[1] ?? "";

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setEmail(draft.trim().toLowerCase());
  }

  return (
    <Card>
      <CardHeader title="Check an address" description="What registration would say to it, and which rule decides." />
      <form onSubmit={handleSubmit} className="p-5">
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            aria-label="Email address"
            type="email"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="someone@example.com"
            required
            className="sm:max-w-md"
          />
          <Button type="submit" variant="secondary" loading={check.isFetching}>
            Check
          </Button>
        </div>
        {check.error && (
          <div className="mt-3">
            <Alert>{check.error.message}</Alert>
          </div>
        )}
        {check.data && email && (
          <div
            className="mt-4 flex flex-col gap-3 rounded-md border border-line bg-raised px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            data-testid="check-result"
          >
            <p className="text-sm">
              {check.data.blocked ? (
                <>
                  <span className="font-medium text-danger">Blocked.</span>{" "}
                  <Mono>{email}</Mono> is refused by the {check.data.source === "list" ? "list's" : ""} rule on{" "}
                  <Mono>{check.data.matched}</Mono>.
                </>
              ) : check.data.matched ? (
                <>
                  <span className="font-medium text-mint">Allowed.</span>{" "}
                  <Mono>{email}</Mono> gets in on the allow rule on <Mono>{check.data.matched}</Mono>.
                </>
              ) : (
                <>
                  <span className="font-medium text-mint">Allowed.</span> No rule covers{" "}
                  <Mono>{domain}</Mono>.
                </>
              )}
            </p>
            {/* The next step, worded for this answer. A list block is lifted by
                an allow rule; an operator rule is lifted by removing it; an
                address with no rule can be blocked. */}
            {canWrite && check.data.matched && check.data.source === "list" && (
              <Button variant="secondary" onClick={() => onAllow(check.data!.matched!)}>
                Allow {check.data.matched}
              </Button>
            )}
            {canWrite && check.data.matched && check.data.source === "operator" && (
              <Button variant="secondary" onClick={() => onLift(check.data!.matched!)}>
                Remove the rule
              </Button>
            )}
            {canWrite && !check.data.matched && (
              <Button variant="secondary" onClick={() => onBlock(domain)}>
                Block {domain}
              </Button>
            )}
          </div>
        )}
      </form>
    </Card>
  );
}

/** Writes an operator rule. Blocks and allows are the same form; the reason is required either way. */
function RuleModal({ draft, onClose }: { draft: Draft | null; onClose: () => void }) {
  const upsert = useUpsertEmailDomain();
  const [form, setForm] = useState<Draft>({ domain: "", action: "block", reason: "" });
  const [seeded, setSeeded] = useState<Draft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  // Re-seeded whenever the modal opens with a new draft, during render rather
  // than in an effect so the first paint already shows it.
  if (draft && draft !== seeded) {
    setSeeded(draft);
    setForm(draft);
    setFormError(null);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    try {
      await upsert.mutateAsync({
        domain: form.domain.trim().toLowerCase(),
        action: form.action,
        reason: form.reason.trim(),
      });
      onClose();
    } catch (cause) {
      setFormError(cause instanceof ApiError ? cause.message : "The rule could not be saved.");
    }
  }

  const blocking = form.action === "block";
  return (
    <Modal
      open={draft !== null}
      onClose={onClose}
      dismissable={!upsert.isPending}
      title={blocking ? "Block a domain" : "Allow a domain"}
      description={
        blocking
          ? "Nobody on it, or on any subdomain of it, will be able to register."
          : "Lifts the list's block for it. Anyone on it can register again."
      }
    >
      <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
        {formError && <Alert>{formError}</Alert>}
        <Field label="Domain" htmlFor="rule-domain">
          <Input
            id="rule-domain"
            value={form.domain}
            onChange={(event) => setForm((current) => ({ ...current, domain: event.target.value }))}
            placeholder="catchmail.io"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            required
          />
        </Field>
        <Field label="Rule" htmlFor="rule-action">
          <Select
            id="rule-action"
            value={form.action}
            onChange={(event) =>
              setForm((current) => ({ ...current, action: event.target.value as Draft["action"] }))
            }
          >
            <option value="block">Block — refuse registration</option>
            <option value="allow">Allow — override the list</option>
          </Select>
        </Field>
        <Field label="Reason" htmlFor="rule-reason" hint="Kept with the rule and written to the audit log.">
          <Input
            id="rule-reason"
            value={form.reason}
            onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))}
            placeholder={blocking ? "Mass registrations, September 2026" : "A real institution; listed wrongly"}
            minLength={3}
            maxLength={500}
            required
          />
        </Field>
        <div className="flex justify-end gap-3">
          <Button type="button" variant="ghost" onClick={onClose} disabled={upsert.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant={blocking ? "danger" : "primary"}
            loading={upsert.isPending}
            disabled={form.domain.trim().length < 3 || form.reason.trim().length < 3}
          >
            {blocking ? "Block domain" : "Allow domain"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Removing an operator rule is asked about, because what it does depends on
 * what is underneath: taking an allow away puts the list's block back, and
 * taking a block away lets the domain register again.
 */
function RemoveModal({ rule, onClose }: { rule: EmailDomainRule | null; onClose: () => void }) {
  const remove = useRemoveEmailDomain();
  const [removeError, setRemoveError] = useState<Error | null>(null);
  return (
    <ConfirmActionModal
      open={rule !== null}
      onClose={onClose}
      title={`Remove the rule on ${rule?.domain ?? ""}`}
      description={
        rule?.listed
          ? "The list names this domain, so its block applies again the moment the rule goes."
          : rule?.action === "allow"
            ? "Nothing else covers this domain. Removing the rule changes nothing until the list names it."
            : "Nothing else covers this domain. Addresses on it can register from now on."
      }
      consequence="Accounts already registered are not affected either way."
      confirmLabel="Remove rule"
      danger={rule?.action === "block"}
      loading={remove.isPending}
      error={removeError}
      onConfirm={() => {
        if (!rule) return;
        setRemoveError(null);
        remove
          .mutateAsync(rule.domain)
          .then(onClose)
          .catch((cause: unknown) =>
            setRemoveError(cause instanceof Error ? cause : new Error("The rule could not be removed.")),
          );
      }}
    />
  );
}
